import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import { Matrix4 } from 'three'

export function getRelativeMatrixOLD(referenceMatrix: Matrix4, offset: number) {
  const newMatrix = referenceMatrix.clone()

  const position = toVector3()
  const quaternion = toQuaternion()
  const scale = toVector3()
  newMatrix.decompose(position, quaternion, scale)

  const offsetVector = toVector3([offset, 0, 0]).applyQuaternion(quaternion.normalize())

  position.add(offsetVector)

  newMatrix.compose(position, quaternion, scale)

  return newMatrix
}

import { LinkedItem } from '@types'
import { isKARBCatalog } from '@utils/catalog/kbrCatalog'
import { getRinconeroRegletaFrontReachMm } from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import {
  getRinconeroHandedness,
  getRinconeroWidthMmFromCatalog,
} from '@utils/sceneObjects/snappedSides'
import { Quaternion, Vector3 } from 'three'

export function getRelativeMatrix(
  referenceMatrix: Matrix4,
  offset: number,
  referenceItem?: LinkedItem,
  heightOffset?: number,
  newItemTipoNormalizado?: string,
  /** Catálogo del item que se va a dibujar (para rinconero-como-nuevo). */
  newItemCatalogID?: string | number | null,
  /** Ancho mm del item nuevo (brazo L izquierdo: desplaza −ancho en el nuevo X). */
  newItemWidthMm?: number | null,
) {
  const newMatrix = referenceMatrix.clone()

  const position = toVector3()
  const quaternion = toQuaternion()
  const scale = toVector3()
  newMatrix.decompose(position, quaternion, scale)

  const rinconeroHand = getRinconeroHandedness(referenceItem?.catalogID)
  const newItemHand = getRinconeroHandedness(newItemCatalogID)
  const isKARB = isKARBCatalog(referenceItem?.catalogID)

  // Casos especiales de posicionamiento vertical
  // Reglas simples:
  // 1. MBJ y MAL se ponen al lado de COL (horizontalmente) - lógica normal
  // 2. MAL encima de MBJ (verticalmente) - casos especiales con offset 0
  const isMBJToMAL = referenceItem?.tipoNormalizado === 'MBJ' && newItemTipoNormalizado === 'MAL'
  const isMALToLow =
    referenceItem?.tipoNormalizado === 'MAL' &&
    (newItemTipoNormalizado === 'MBJ' || newItemTipoNormalizado === 'COL')

  // ── Referencia = rinconero: colocar el nuevo item en el giro en L ──────────
  if (rinconeroHand === 'right' || rinconeroHand === 'left') {
    // Esquina L = ancho del rinconero. Empujón Z = puerta+regleta.
    const lCornerMm =
      Number(referenceItem?.width) > 0 ? Number(referenceItem.width) : offset
    const KARB_OFFSET_ADJUSTMENT = isKARB ? -50 : 0
    const reachMm = getRinconeroRegletaFrontReachMm(referenceItem)
    const depthMm =
      (reachMm > 0 ? reachMm : Number(referenceItem?.thickness || 0)) + KARB_OFFSET_ADJUSTMENT

    if (rinconeroHand === 'right') {
      // L en +X: origen en esquina derecha, −90° Y, empujar por reach.
      const offsetVector = toVector3([lCornerMm, 0, 0]).applyQuaternion(quaternion)
      const newPosition = position.clone().add(offsetVector)
      const rotationAdjustment = new Quaternion().setFromAxisAngle(
        new Vector3(0, 1, 0),
        -Math.PI / 2,
      )
      const newQuaternion = quaternion.clone().multiply(rotationAdjustment)
      const zAxis = new Vector3(0, 0, 1).applyQuaternion(quaternion)
      newPosition.add(zAxis.multiplyScalar(depthMm))
      return new Matrix4().compose(newPosition, newQuaternion, scale)
    }

    // Izquierda — espejo del derecho: L en x=0, +90° Y, empujón Z = reach.
    // Tras +90°, +X nuevo apunta al rinconero: restar el ANCHO DEL ITEM NUEVO
    // (no el del rinconero) para que quede fuera a ras de la regleta.
    const newPosition = position.clone()
    const rotationAdjustment = new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), Math.PI / 2)
    const newQuaternion = quaternion.clone().multiply(rotationAdjustment)
    const zAxis = new Vector3(0, 0, 1).applyQuaternion(quaternion)
    newPosition.add(zAxis.multiplyScalar(depthMm))
    const placedWidth = Number(newItemWidthMm)
    if (placedWidth > 0) {
      const xAxisNew = new Vector3(1, 0, 0).applyQuaternion(newQuaternion)
      newPosition.add(xAxisNew.multiplyScalar(-placedWidth))
    }
    return new Matrix4().compose(newPosition, newQuaternion, scale)
  }

  // ── Nuevo item = rinconero, referencia = unidad normal ────────────────────
  // Derecho: offset +X del ancho de la referencia (su left toca el right del MBJ).
  // Izquierdo: colocar a la IZQUIERDA (su right toca el left del MBJ); si no,
  // queda en línea sin L y “no voltea”.
  if (newItemHand && !rinconeroHand) {
    const rinconeroW =
      getRinconeroWidthMmFromCatalog(newItemCatalogID) ??
      (Number(offset) > 0 ? Number(offset) : 1000)
    if (newItemHand === 'left') {
      const offsetVector = toVector3([-rinconeroW, heightOffset ?? 0, 0]).applyQuaternion(
        quaternion,
      )
      const newPos = position.clone().add(offsetVector)
      return new Matrix4().compose(newPos, quaternion, scale)
    }
    // right: caer al offset normal (+ancho referencia) más abajo
  }

  if (isMBJToMAL) {
    // Caso especial: MBJ -> MAL: colocar encima del MBJ
    const referenceHeight = referenceItem?.height || 0

    const refX = position.x
    const refY = position.y
    const refZ = position.z

    const newY = refY + referenceHeight

    let newX: number
    let newZ: number

    if (offset === 0) {
      newX = refX
      newZ = refZ
    } else {
      const offsetVector = toVector3([offset, heightOffset ?? 0, 0]).applyQuaternion(
        quaternion.normalize(),
      )
      const offsetPosition = position.clone().add(offsetVector)
      newX = offsetPosition.x
      newZ = offsetPosition.z
    }

    const newPosition = new Vector3(newX, newY, newZ)
    return new Matrix4().compose(newPosition, quaternion, scale)
  } else if (isMALToLow) {
    const malX = position.x
    const malZ = position.z
    const newY = 0

    let newX: number
    let newZ: number

    if (offset === 0) {
      newX = malX
      newZ = malZ
    } else {
      const offsetVector = toVector3([offset, heightOffset ?? 0, 0]).applyQuaternion(
        quaternion.normalize(),
      )
      const offsetPosition = position.clone().add(offsetVector)
      newX = offsetPosition.x
      newZ = offsetPosition.z
    }

    const newPosition = new Vector3(newX, newY, newZ)
    return new Matrix4().compose(newPosition, quaternion, scale)
  } else {
    const offsetVector = toVector3([offset, heightOffset ?? 0, 0]).applyQuaternion(
      quaternion.normalize(),
    )
    const newPos = position.clone().add(offsetVector)

    return newMatrix.compose(newPos, quaternion, scale)
  }
}
